import type { Board } from "../board";
import { Direction, directionDelta } from "../direction";
import { BallisticMovementEffect } from "../effects/ballisticMovementEffect";
import { Timing } from "../effects/effect";
import { ParticleEffect } from "../effects/particleEffect";
import { TextPopupEffect } from "../effects/textPopupEffect";
import { ParticleType } from "../particleType";
import type { Player } from "../player";
import { BlackHoleProjectile } from "../projectiles/blackHoleProjectile";
import { Chasm } from "../things/chasm";
import { LavaPool } from "../things/lavaPool";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { Weapon } from "../weapon";
import { WeaponVisual } from "../weaponVisual";
import { XY } from "../xy";

export class Jetpack extends Weapon {
	static key = "Jetpack";

	static readonly CHARGES_PER_BOX = 5;
	static readonly MAX_JUMP_DISTANCE = 8;
	static readonly MIN_JUMP_DISTANCE = 3;
	static readonly AIR_TIME_MS_PER_CELL = 200;
	static readonly JUMP_HEIGHT = 2;
	static readonly LIFTOFF_DUST_COUNT = 10;

	constructor(owner: Player) {
		super(owner, 0);
	}

	override canAutoFire(): boolean {
		return false;
	}

	override getWeaponVisual(): WeaponVisual {
		return WeaponVisual.NONE;
	}

	override pullTrigger(direction: Direction, board: Board): boolean {
		if (this.ammo <= 0) {
			board.turnAbortedByPlayer = true;
			return false;
		}

		const target = this.findLandingSpot(direction, board);
		if (target === null) {
			board.addNonPhysicalThing(new Sound(SoundId.TERMINAL_ERROR));
			board.addEffect(new TextPopupEffect(this.owner.xy, "popup_jetpack_noSafeSpot"));
			board.turnAbortedByPlayer = true;
			return false;
		}

		this.ammo--;
		this.executeJump(target, board);
		board.stats.recordShotFired(Jetpack.key);
		return true;
	}

	fire(_direction: Direction, _board: Board): void {
		// All work is done in pullTrigger (we need to validate before consuming a turn/ammo).
	}

	private findLandingSpot(direction: Direction, board: Board): XY | null {
		const delta = directionDelta(direction);
		const origin = this.owner.xy;

		// MAX/MIN_JUMP_DISTANCE are euclidean cell distances. Diagonal steps cover sqrt(2)
		// cells each, so an 8-step diagonal jump would travel ~11.3 — much farther than a
		// cardinal one. Floor the step count so the actual jump never exceeds the cap.
		const stepLength = Math.hypot(delta.x, delta.y);
		const maxSteps = Math.floor(Jetpack.MAX_JUMP_DISTANCE / stepLength);
		const minSteps = Math.floor(Jetpack.MIN_JUMP_DISTANCE / stepLength);

		for (let dist = maxSteps; dist >= minSteps; dist--) {
			const target = new XY(origin.x + delta.x * dist, origin.y + delta.y * dist);
			if (!board.isInBounds(target)) continue;

			if (!this.isPathClear(origin, delta, dist, board)) continue;

			if (!this.canLandOn(board, target)) continue;

			if (!board.hasMob(target)) return target;

			const neighbor = this.findLandingNeighbor(target, board);
			if (neighbor !== null) return neighbor;
		}
		return null;
	}

	private isPathClear(origin: XY, delta: XY, dist: number, board: Board): boolean {
		for (let step = 1; step < dist; step++) {
			const cell = new XY(origin.x + delta.x * step, origin.y + delta.y * step);
			if (!board.isInBounds(cell)) return false;
			if (!this.canPassOver(board, cell)) return false;
		}
		return true;
	}

	private findLandingNeighbor(target: XY, board: Board): XY | null {
		for (const neighbor of target.getNeighbors()) {
			if (!board.isInBounds(neighbor)) continue;
			if (neighbor.equals(this.owner.xy)) continue;
			if (!this.canLandOn(board, neighbor)) continue;
			if (board.hasMob(neighbor)) continue;
			return neighbor;
		}
		return null;
	}

	private canPassOver(board: Board, xy: XY): boolean {
		if (BlackHoleProjectile.blocksCell(board, xy)) return false;
		if (board.isPassable(xy)) return true;
		return board.getThingsAt(xy).some((t) => t instanceof LavaPool || t instanceof Chasm);
	}

	private canLandOn(board: Board, xy: XY): boolean {
		if (BlackHoleProjectile.blocksCell(board, xy)) return false;
		// Lava/chasm with `passable: true` carries a bridge (e.g. MetalGridFloor) — landable.
		// Bare lava/chasm has `passable: false`, which makes `isPassable` return false.
		return board.isPassable(xy);
	}

	private executeJump(target: XY, board: Board): void {
		const oldXY = this.owner.xy;
		this.owner.isJumping = true;
		this.owner.xy = target;

		board.addEffect(new ParticleEffect(oldXY, ParticleType.STEP_DUST, Jetpack.LIFTOFF_DUST_COUNT));

		const airTime = oldXY.distanceTo(target) * Jetpack.AIR_TIME_MS_PER_CELL;
		const effect = new BallisticMovementEffect(this.owner, oldXY, target, airTime);
		effect.height = Jetpack.JUMP_HEIGHT;
		// Order -1: jump finishes before any reactive pre-effects on the landing tile.
		effect.setAnimationOrder(-1);
		board.addEffect(effect);

		const thrust = new Sound(SoundId.JETPACK_THRUST, oldXY);
		thrust.setTiming(Timing.BEFORE_STATE_UPDATE);
		thrust.setAnimationOrder(-1);
		board.addNonPhysicalThing(thrust);
	}

	static deserialize(data: any, owner: Player): Jetpack {
		const gun = new Jetpack(owner);
		gun.ammo = data.ammo;
		return gun;
	}
}
